import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Switch,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { GCModal } from "../modals/GCModal";
import { CloseCircleIcon } from "../icons/CloseIcon";
import { List } from "../../api/lists";
import {
  useReadingListEditDraft,
  useReadingListEditMutations,
} from "./useReadingListEdit";

const NAME_MAX = 128;

export interface ReadingListEditModalProps {
  list: List;
  isOpen: boolean;
  onClose: () => void;
}

export function ReadingListEditModal({
  list,
  isOpen,
  onClose,
}: ReadingListEditModalProps) {
  const [t] = useTranslation();
  const toast = useToast();

  // The shared draft hook keeps title + description in local-edit state and
  // exposes `isDirty` / `canSave`. We layer a local public/private flag on
  // top because visibility is its own mutation (toggleVisibility) and the
  // user might toggle it without touching name/desc.
  const draft = useReadingListEditDraft(list);
  const mutations = useReadingListEditMutations(list);
  const [isPublic, setIsPublic] = useState<boolean>(list.is_public);

  useEffect(() => {
    if (isOpen) setIsPublic(list.is_public);
  }, [isOpen, list.is_public]);

  useEffect(() => {
    if (isOpen) draft.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const visibilityChanged = isPublic !== list.is_public;
  const hasChanges = draft.isDirty || visibilityChanged;
  const canSave = !draft.isDirty || draft.canSave;
  const isSaving =
    mutations.saveMetadata.isPending || mutations.toggleVisibility.isPending;

  const handleCancel = () => {
    if (isSaving) return;
    draft.reset();
    setIsPublic(list.is_public);
    onClose();
  };

  const handleSave = async () => {
    if (!hasChanges || !canSave || isSaving) return;
    try {
      // Visibility goes first because saveMetadata refetches the list,
      // which would otherwise reset the toggle's local state mid-save.
      if (visibilityChanged) {
        await mutations.toggleVisibility.mutateAsync(isPublic);
      }
      if (draft.isDirty) {
        await mutations.saveMetadata.mutateAsync({
          name: draft.draftName.trim(),
          description: draft.draftDescription.trim() || undefined,
        });
      }
      onClose();
    } catch {
      toast({
        title: t("screens.shelf.readingLists.detail.editor.editModal.saveError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const header = (
    <Flex align="center" justify="space-between" px="22px" py="16px">
      <Text
        fontFamily="Bricolage Grotesque"
        fontWeight={500}
        fontSize="15px"
        color="dune.dune"
      >
        {t("screens.shelf.readingLists.detail.editor.editModal.title")}
      </Text>
      <IconButton
        aria-label={t(
          "screens.shelf.readingLists.detail.editor.editModal.closeAria",
        )}
        icon={<CloseCircleIcon w="16px" h="16px" />}
        size="sm"
        variant="ghost"
        color="dune.700"
        _hover={{ bg: "transparent.white.10", color: "dune.dune" }}
        onClick={handleCancel}
        isDisabled={isSaving}
      />
    </Flex>
  );

  const body = (
    <Flex direction="column" gap="20px" px="32px" py="24px">
      <Box>
        <FormLabel
          fontFamily="Bricolage Grotesque"
          fontSize="13px"
          color="dune.700"
          letterSpacing="0.05em"
          textTransform="uppercase"
          mb="8px"
        >
          {t("screens.shelf.readingLists.detail.editor.editModal.nameLabel")}
        </FormLabel>
        <Input
          value={draft.draftName}
          onChange={(e) => draft.setDraftName(e.target.value)}
          placeholder={t(
            "screens.shelf.readingLists.detail.editor.editModal.namePlaceholder",
          )}
          autoFocus
          maxLength={NAME_MAX}
          size="lg"
          bg="transparent.white.05"
          border="1px solid"
          borderColor="transparent.white.20"
          borderRadius="10px"
          color="dune.dune"
          fontSize="16px"
          _placeholder={{ color: "dune.800" }}
          _focus={{
            borderColor: "blaze.blaze",
            bg: "transparent.white.10",
            boxShadow: "none",
          }}
        />
        <Flex justify="space-between" mt="8px">
          <Text fontSize="11px" color="dune.700" letterSpacing="0.2px">
            {t(
              "screens.shelf.readingLists.detail.editor.editModal.nameRequired",
            )}
          </Text>
          <Text fontSize="11px" color="dune.700" letterSpacing="0.2px">
            {t(
              "screens.shelf.readingLists.detail.editor.editModal.nameCounter",
              { count: draft.draftName.length, max: NAME_MAX },
            )}
          </Text>
        </Flex>
      </Box>

      <Box>
        <FormLabel
          fontFamily="Bricolage Grotesque"
          fontSize="13px"
          color="dune.700"
          letterSpacing="0.05em"
          textTransform="uppercase"
          mb="8px"
        >
          {t(
            "screens.shelf.readingLists.detail.editor.editModal.descriptionLabel",
          )}
        </FormLabel>
        <Textarea
          value={draft.draftDescription}
          onChange={(e) => draft.setDraftDescription(e.target.value)}
          placeholder={t(
            "screens.shelf.readingLists.detail.editor.editModal.descriptionPlaceholder",
          )}
          minH="120px"
          resize="vertical"
          bg="transparent.white.05"
          border="1px solid"
          borderColor="transparent.white.20"
          borderRadius="10px"
          color="dune.dune"
          fontSize="16px"
          _placeholder={{ color: "dune.800" }}
          _focus={{
            borderColor: "blaze.blaze",
            bg: "transparent.white.10",
            boxShadow: "none",
          }}
        />
        <Text mt="8px" fontSize="11px" color="dune.700">
          {t(
            "screens.shelf.readingLists.detail.editor.editModal.descriptionOptional",
          )}
        </Text>
      </Box>

      <FormControl
        display="grid"
        gridTemplateColumns="auto 1fr"
        gap="12px"
        alignItems="center"
        p="12px 14px"
        border="1px solid"
        borderColor="transparent.white.10"
        borderRadius="10px"
        bg="transparent.white.05"
      >
        <Switch
          id="reading-list-edit-privacy"
          size="sm"
          variant="brandSwitch"
          isChecked={isPublic}
          onChange={(e) => setIsPublic(e.target.checked)}
        />
        <FormLabel htmlFor="reading-list-edit-privacy" m={0} cursor="pointer">
          <Text fontSize="13px" fontWeight={500} color="dune.dune">
            {isPublic
              ? t(
                  "screens.shelf.readingLists.detail.editor.editModal.visibilityPublicLabel",
                )
              : t(
                  "screens.shelf.readingLists.detail.editor.editModal.visibilityPrivateLabel",
                )}
          </Text>
          <Text fontSize="11px" color="dune.700" mt="2px">
            {isPublic
              ? t(
                  "screens.shelf.readingLists.detail.editor.editModal.visibilityPublicHelper",
                )
              : t(
                  "screens.shelf.readingLists.detail.editor.editModal.visibilityPrivateHelper",
                )}
          </Text>
        </FormLabel>
      </FormControl>
    </Flex>
  );

  const footer = (
    <Flex
      align="center"
      justify="flex-end"
      gap="8px"
      px="22px"
      py="16px"
      bg="transparent.white.05"
    >
      <Button
        variant="tertiary"
        size="sm"
        onClick={handleCancel}
        isDisabled={isSaving}
      >
        {t("screens.shelf.readingLists.detail.editor.editModal.cancel")}
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSave}
        isDisabled={!hasChanges || !canSave}
        isLoading={isSaving}
      >
        {t("screens.shelf.readingLists.detail.editor.editModal.save")}
      </Button>
    </Flex>
  );

  return (
    <GCModal
      isOpen={isOpen}
      onClose={handleCancel}
      header={header}
      body={body}
      footer={footer}
      stickyFooter
      width="560px"
      maxWidth="560px"
      locked={isSaving}
    />
  );
}
